/* import __COLOCATED_TEMPLATE__ from './add-source-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-default-task-ember-concurrency */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type ContentImportService from 'embercom/services/content-import-service';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { TrackedArray } from 'tracked-built-ins';
import { get, post } from 'embercom/lib/ajax';
import isValidUrl, {
  isValidUrlWithProtocol,
  urlDomainsMatch,
  urlWithHttpsPrefix,
  urlWithoutProtocol,
  urlWithoutProtocolAndWww,
  urlWithoutQueryParams,
} from 'embercom/lib/url-validator';
import { EntityType } from 'embercom/models/data/entity-types';
import { TriggerSources } from 'embercom/objects/content-service/content-imports/constants/trigger-sources';
import { action } from '@ember/object';
import type GuideLibraryService from 'embercom/services/guide-library-service';
import type IntlService from 'embercom/services/intl';
import { isValidCssSelector } from 'embercom/lib/knowledge-hub/constants';
import { type addModalUpdateParams } from './advanced-settings-websync';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { type ActorData, ActorName } from 'embercom/models/content-service/content-import-source';
import { type KnowledgeUsageSummary } from 'embercom/components/knowledge-hub/overview/types';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import type RouterService from '@ember/routing/router-service';
import type { SitemapDomain, SitemapPage } from './sitemap-domain-selector';
import type { SelectedDomain as SitemapSelectedDomain } from './sitemap-domain-selector';

export interface SelectedDomain {
  domain: string;
  selectedPages: string[];
  urlsToExclude: string[];
  includeUrls: string[];
  startUrls: string[];
  additionalUrls: string[];
}

export enum ModalStep {
  UrlInput = 'url-input',
  SitemapSelection = 'sitemap-selection',
}

interface Args {
  modal: any;
  onModalClose: () => void;
  onNavigationBack?: () => void;
  showBackButton?: boolean;
  isNewSource?: boolean;
  source?: ContentImportSource | undefined | null;
  syncSource?: SyncSourceWrapper | undefined | null;
}

export interface SourceParams {
  app_id: string;
  url: string;
  additional_urls: string[];
  trigger_source: TriggerSources;
  content_type: EntityType;
  admin_id: number;
  exclude_url_globs: string[];
  include_url_globs: string[];
  remove_element_css_selectors: string[];
  click_element_css_selector?: string;
  use_sitemap: boolean;
  wait_for_selector?: string;
  actor_data?: ActorData;
  website_name?: string;
}

export interface SourceStatusParams {
  status: 'active' | 'deactivated';
}

export interface DuplicateSource {
  folderId: number | null;
  offendingUrl: string | null;
}

export interface UrlCheckResponse {
  is_accessible: boolean;
  status_code: number;
  error?: string;
}

// Server response message - not user-facing. TODO: we will add error type to handle user facing messages.
// eslint-disable-next-line @intercom/intercom/no-bare-strings
const BLOCKED_URL_ERROR_MESSAGE = 'URL is not allowed';

export default class AddSourceModal extends Component<Args> {
  @service declare contentImportService: ContentImportService;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;
  @service declare guideLibraryService: GuideLibraryService;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare router: RouterService;
  @tracked isNewSource: boolean = this.args.isNewSource ?? true;
  @tracked source = this.args.source || undefined;
  @tracked syncSource = this.args.syncSource || undefined;
  @tracked currentStep: ModalStep = ModalStep.UrlInput;
  @tracked pages: SitemapPage[] = [];
  @tracked allSubdomains: SitemapDomain[] = [];
  @tracked availableSubdomains: { text: string; value: string }[] = [];
  @tracked subdomains: SitemapDomain[] = [];
  @tracked domain: SitemapDomain | null = null;
  @tracked selectedSubdomains: string[] = [];
  @tracked isLoadingUrl = false;
  @tracked urlCheckResponse: UrlCheckResponse | null = null;
  @tracked isSyncableWebsite: boolean | null = null;
  @tracked sourceUrl = this.source?.url || '';
  @tracked additionalUrls = this.source?.additionalUrls?.toString() || '';
  @tracked submissionErrorTranslationKey: string | undefined = undefined;
  @tracked openSectionId = '';
  @tracked excludedUrls = this.source
    ? new TrackedArray<string>([...this.source.excludeUrlGlobs])
    : new TrackedArray<string>([]);
  @tracked includeUrls = this.source
    ? new TrackedArray<string>([...this.source.includeUrlGlobs])
    : new TrackedArray<string>([]);
  @tracked removeCssSelectors = this.source?.removeCssSelectors?.toString() || '';
  @tracked clickCssSelector = this.source?.clickCssSelector?.toString() || '';
  @tracked useSitemap = this.source?.useSitemap ?? true;
  @tracked waitForSelector = this.source?.waitForSelector?.toString() || '';
  @tracked relativeDateMonths = this.source?.relativeDateMonths;
  @tracked dateSelector = this.source?.dateSelector;
  @tracked includePagesWithNoDate = this.source?.includePagesWithNoDate ?? false;
  @tracked useDateCrawler = this.source?.actorData?.actor_name === ActorName.DatedContentCrawler;
  @tracked startUrlDuplicateSource: DuplicateSource | null = null;
  @tracked additionalUrlsDuplicateSource: DuplicateSource | null = null;
  @tracked hasPdfUrl = false;
  @tracked websiteName = '';
  @tracked selectedDomains: SelectedDomain[] = [];
  @tracked selectedPages: string[] = [];
  @tracked hasUrlErrors = false;
  @tracked specificPageToSelect: SitemapPage | undefined = undefined;

  @use usageSummaryLoader = AsyncData<KnowledgeUsageSummary>(async () => {
    if (!this.knowledgeHubService.usageSummary) {
      await this.knowledgeHubService.fetchKnowledgeUsageSummary();
    }
    return this.knowledgeHubService.usageSummary;
  });

  constructor(owner: unknown, args: Args) {
    super(owner, args);
  }

  get usageSummary(): KnowledgeUsageSummary | undefined {
    return this.usageSummaryLoader.value;
  }

  get isLoading(): boolean {
    return this.usageSummaryLoader.isLoading;
  }

  get urlErrorMessage(): any {
    if (this.hasPdfUrl) {
      return this.intl.t('ai-content-library.sources.add-source-modal.pdf-warning', {
        htmlSafe: true,
      });
    }

    if (this.sourceUrl.length > 255) {
      return this.intl.t('ai-content-library.sources.add-source-modal.url-too-long', {
        htmlSafe: true,
      });
    }

    if (this.startUrlDuplicateSource?.folderId) {
      let otherSourceRedirect = this.router.urlFor(
        'apps.app.knowledge-hub.folder',
        this.startUrlDuplicateSource.folderId,
      );
      return this.intl.t('ai-content-library.sources.add-source-modal.duplicate-url', {
        otherSourceRedirect,
        htmlSafe: true,
      });
    }

    if (this.urlCheckResponse?.status_code === 404) {
      return this.intl.t('ai-content-library.sources.add-source-modal.website-not-found', {
        htmlSafe: true,
      });
    }

    if (
      !this.isSyncableWebsite &&
      this.isSyncableWebsite !== null &&
      this.urlCheckResponse?.status_code !== 404
    ) {
      if (this.urlCheckResponse?.error?.includes(BLOCKED_URL_ERROR_MESSAGE)) {
        if (this.isAppStoreUrl(this.sourceUrl)) {
          return this.intl.t(
            'ai-content-library.sources.add-source-modal.blocked-website-app-store',
            {
              htmlSafe: true,
            },
          );
        }
        return this.intl.t('ai-content-library.sources.add-source-modal.blocked-website', {
          url: this.sourceUrl,
          htmlSafe: true,
        });
      } else {
        return this.intl.t('ai-content-library.sources.add-source-modal.not-syncable-website', {
          url: this.sourceUrl,
          htmlSafe: true,
        });
      }
    }

    if (!isValidUrl(this.sourceUrl) && this.sourceUrl !== '') {
      return this.intl.t('ai-content-library.sources.add-source-modal.validation-error', {
        url: this.sourceUrl,
      });
    }

    if (this.sourceUrl === '') {
      return this.intl.t('ai-content-library.sources.add-source-modal.validation-error', {
        url: this.sourceUrl,
      });
    }
  }

  private isAppStoreUrl(url: string): boolean {
    let appStoreDomains = ['play.google.com', 'apps.apple.com', 'itunes.apple.com'];
    try {
      let urlWithProtocol = url.includes('://') ? url : `https://${url}`;
      let hostname = new URL(urlWithProtocol).hostname.toLowerCase();
      return appStoreDomains.some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
      );
    } catch {
      return false;
    }
  }

  private isSameDomain(domain1: string, domain2: string): boolean {
    let normalizeDomain = (domain: string): string => {
      let normalizedDomain = domain.replace(/^https?:\/\//, '');

      normalizedDomain = normalizedDomain.replace(/^www\./, '');

      normalizedDomain = normalizedDomain.split('/')[0];

      return normalizedDomain.toLowerCase();
    };

    return normalizeDomain(domain1) === normalizeDomain(domain2);
  }

  private getDuplicateSource(url: string): DuplicateSource {
    let otherSources = this.usageSummary?.[EntityType.ExternalContent]?.sync_sources || [];
    let inputUrlWithoutProtocol: string = urlWithoutProtocol(url);
    for (let otherSource of otherSources) {
      if (inputUrlWithoutProtocol === urlWithoutProtocol(otherSource.start_url)) {
        return {
          offendingUrl: url,
          folderId: otherSource.folder_id || null,
        };
      }
      let additionalUrlsWithoutProtocol =
        otherSource.additional_urls?.map((url) => urlWithoutProtocol(url)) || [];
      if (additionalUrlsWithoutProtocol.includes(inputUrlWithoutProtocol)) {
        return {
          offendingUrl: url,
          folderId: otherSource.folder_id || null,
        };
      }
    }
    return {
      offendingUrl: null,
      folderId: null,
    };
  }

  @action
  resetErrorChecks() {
    this.hasUrlErrors = false;
    this.startUrlDuplicateSource = null;
    this.additionalUrlsDuplicateSource = null;
    this.hasPdfUrl = false;
    this.isSyncableWebsite = null;
  }

  @action
  async checkUrlRules() {
    this.trackAnalyticsEvent('inputed', 'url_source_input');

    if (this.sourceUrl.endsWith('/')) {
      this.sourceUrl = this.sourceUrl.replace(/\/+$/, '');
    }

    if (!this.appService.app.canUseFeature('team-knowledge-foundations-duplicate-url-killswitch')) {
      let duplicateSource = this.getDuplicateSource(this.sourceUrl);
      this.startUrlDuplicateSource = duplicateSource;
    }

    this.hasPdfUrl = this.sourceUrl.trim().toLowerCase().endsWith('.pdf');

    if (this.hasPdfUrl || this.startUrlDuplicateSource?.folderId != null) {
      this.isSyncableWebsite = false;
      return;
    }

    // Skip URL validation if feature flag is enabled
    if (
      this.appService.app.canUseFeature('team-knowledge-foundations-disable-website-status-check')
    ) {
      this.isSyncableWebsite = true;
      return;
    } else {
      await this.checkUrl();
    }
  }

  @action async checkAdditionalUrlsAreNotAlreadySynced() {
    this.trackAnalyticsEvent('inputed', 'additional_urls_input');

    if (!this.appService.app.canUseFeature('team-knowledge-foundations-duplicate-url-killswitch')) {
      for (let additionalUrl of this.additionalUrlsArray) {
        let duplicateSource = this.getDuplicateSource(additionalUrl);
        if (duplicateSource.folderId !== null) {
          this.additionalUrlsDuplicateSource = duplicateSource;
          return;
        }
      }
      this.additionalUrlsDuplicateSource = {
        offendingUrl: null,
        folderId: null,
      };
    }
  }

  get isUrlInvalid(): boolean {
    return (
      this.startUrlDuplicateSource?.folderId != null ||
      !isValidUrl(this.sourceUrl) ||
      this.sourceUrl.length > 255 ||
      this.hasPdfUrl ||
      (this.isNewSource && this.isSyncableWebsite === false) ||
      this.sourceUrl === ''
    );
  }

  get isAddingNewSource(): boolean {
    return (
      taskFor(this.addNewSource).isRunning ||
      taskFor(this.saveSource).isRunning ||
      taskFor(this.saveAndResyncSource).isRunning
    );
  }

  get canProceedToNextStep(): boolean {
    if (this.appService.app.canUseNewWebsiteSync) {
      switch (this.currentStep) {
        case ModalStep.UrlInput:
          return !this.isUrlInvalid && this.sourceUrl.length > 0 && !this.isLoadingUrl;
        case ModalStep.SitemapSelection:
          return this.selectedDomains.length > 0;
        default:
          return false;
      }
    }

    return (
      !this.isUrlInvalid && this.sourceUrl.length > 0 && !this.isLoadingUrl && !this.formIsInvalid
    );
  }

  get showBackButton(): boolean {
    if (this.args.showBackButton) {
      return this.args.showBackButton;
    }

    return this.currentStep !== ModalStep.UrlInput;
  }

  get modalTitle(): string {
    switch (this.currentStep) {
      case ModalStep.UrlInput:
        return this.intl.t('ai-content-library.sources.add-source-modal.heading');
      case ModalStep.SitemapSelection:
        return this.intl.t('ai-content-library.sources.add-source-modal.sitemap-selection.heading');
      default:
        return '';
    }
  }

  get isAdditionalUrlsInvalid(): boolean {
    if (this.sourceUrl === '') {
      return true;
    }

    if (this.additionalUrls.length >= 65000) {
      return true;
    }

    if (this.additionalUrls !== '') {
      for (let additionalUrl of this.additionalUrls.split(',')) {
        additionalUrl = additionalUrl.trim();
        if (additionalUrl !== '') {
          // Ignore empty strings caused by trailing or stray commas
          if (
            !isValidUrlWithProtocol(additionalUrl) ||
            !isValidUrl(this.sourceUrl) ||
            !this.checkAdditionalUrlDomainMatch(this.sourceUrl, additionalUrl) ||
            this.additionalUrlsDuplicateSource?.folderId != null
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private checkAdditionalUrlDomainMatch(url1: string, url2: string): boolean {
    if (this.appService.app.canUseNewWebsiteSync) {
      return true;
    }

    return urlDomainsMatch(urlWithHttpsPrefix(url1), urlWithHttpsPrefix(url2));
  }

  get additionalUrlsArray(): string[] {
    let urls = this.additionalUrls
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url !== '')
      .uniq();

    if (this.domain && !this.isDomainSelected()) {
      urls = urls.filter((url) => url !== this.domain!.domain);
    }

    return urls;
  }

  get removeCssSelectorsArray(): string[] {
    return this.removeCssSelectors
      .split(',')
      .map((selector) => selector.trim())
      .filter((selector) => selector !== '')
      .uniq();
  }

  get excludedUrlGlobs(): string[] {
    return Array.from(this.excludedUrls).filter((glob) => glob !== '');
  }

  get isRemoveCssSelectorInvalid(): boolean {
    return this.removeCssSelectors !== '' && !isValidCssSelector(this.removeCssSelectors);
  }

  get isClickCssSelectorInvalid(): boolean {
    return this.clickCssSelector !== '' && !isValidCssSelector(this.clickCssSelector);
  }

  get isWaitForSelectorInvalid(): boolean {
    return this.waitForSelector !== '' && !isValidCssSelector(this.waitForSelector);
  }

  get isDateSelectorInvalid(): boolean {
    return !!this.dateSelector && !isValidCssSelector(this.dateSelector);
  }

  get isRelativeDatePresentAndLessThanOne(): boolean {
    return this.relativeDateMonths !== undefined && this.relativeDateMonths < 1;
  }

  get formIsInvalid(): boolean {
    return (
      this.isUrlInvalid ||
      this.isAdditionalUrlsInvalid ||
      this.isRemoveCssSelectorInvalid ||
      this.isWaitForSelectorInvalid ||
      this.isClickCssSelectorInvalid ||
      this.isDateSelectorInvalid ||
      this.isRelativeDatePresentAndLessThanOne
    );
  }

  get learnMoreUrl() {
    return 'https://www.intercom.com/help/en/articles/9357945-syncing-and-managing-public-urls';
  }

  get isLoadingPageStructure(): boolean {
    return taskFor(this.fetchPageStructure).isRunning;
  }

  @action
  async goToNextStep() {
    this.hasUrlErrors = false;
    if (!this.canProceedToNextStep) {
      if (this.currentStep === ModalStep.UrlInput) {
        this.hasUrlErrors = true;
      }
      return;
    }

    if (this.appService.app.canUseNewWebsiteSync) {
      switch (this.currentStep) {
        case ModalStep.UrlInput:
          await this.checkUrlRules();

          if (this.isSyncableWebsite === true) {
            await Promise.allSettled([
              taskFor(this.fetchPageStructure).perform(this.sourceUrl),
              taskFor(this.fetchSubdomains).perform(),
            ]);
            this.currentStep = ModalStep.SitemapSelection;
          } else {
            this.hasUrlErrors = true;
          }

          break;
        case ModalStep.SitemapSelection:
          if (this.isNewSource) {
            await taskFor(this.addNewSource).perform();
          } else {
            await taskFor(this.saveAndResyncSource).perform();
          }
          break;
      }
    } else {
      // Old path: if canUseNewWebsiteSync is false
      await this.checkUrlRules();

      if (this.isSyncableWebsite === true) {
        if (this.isNewSource) {
          await taskFor(this.addNewSource).perform();
        } else {
          await taskFor(this.saveAndResyncSource).perform();
        }
      }
    }
  }

  @action
  goToPreviousStep() {
    if (
      this.args.onNavigationBack &&
      (this.currentStep === ModalStep.UrlInput || this.currentStep === undefined)
    ) {
      this.args.onNavigationBack();
    } else {
      this.currentStep = ModalStep.UrlInput;

      this.resetForm();
    }
  }

  @action
  async getDomainPages(domain: SitemapDomain) {
    await taskFor(this.fetchDomainPages).perform(domain);
  }

  @action
  handleDomainSelectionChange(selection: SitemapSelectedDomain) {
    if (!selection || selection.domain === undefined) {
      return;
    }

    let existingIndex = this.selectedDomains.findIndex((d) => d && d.domain === selection.domain);
    let domainUrl = selection.domain;

    let isDomainDeselected = !selection.selectedPages?.length && !selection.startUrls?.length;

    if (existingIndex >= 0) {
      if (isDomainDeselected) {
        // Remove the domain entirely from selectedDomains
        this.selectedDomains = this.selectedDomains.filter((_, i) => i !== existingIndex);

        this.removeFromAdditionalUrls(domainUrl);
      } else {
        let updatedDomains = [...this.selectedDomains];
        updatedDomains[existingIndex] = selection;
        this.selectedDomains = updatedDomains;
      }
    } else if (!isDomainDeselected) {
      this.selectedDomains = [...this.selectedDomains, selection];
    }

    this.rebuildUrlCollections();
  }

  private removeFromAdditionalUrls(domainToRemove: string): void {
    let updatedAdditionalUrls = this.additionalUrlsArray.filter((url) => {
      return !this.isSameDomain(url, domainToRemove);
    });

    this.additionalUrls = updatedAdditionalUrls.join(',');
  }

  private rebuildUrlCollections(): void {
    let includeUrls: string[] = [];
    let excludeUrls: string[] = [];
    let additionalUrlDomains: string[] = [];

    this.selectedDomains.forEach((domain) => {
      domain.urlsToExclude.forEach((url) => {
        if (!excludeUrls.includes(url)) {
          excludeUrls.push(url);
        }
      });

      domain.includeUrls.forEach((url) => {
        if (!includeUrls.includes(url)) {
          includeUrls.push(url);
        }
      });

      // Use additionalUrls from domain selection (which combines initial + dynamic URLs)
      domain.additionalUrls.forEach((url) => {
        if (isValidUrl(url) && !additionalUrlDomains.includes(url)) {
          additionalUrlDomains.push(url);
        }
      });
    });

    if (this.source && this.source.excludeUrlGlobs && this.source.excludeUrlGlobs.length > 0) {
      this.source.excludeUrlGlobs.forEach((url) => {
        if (!excludeUrls.includes(url)) {
          excludeUrls.push(url);
        }
      });
    }

    this.excludedUrls = new TrackedArray<string>(excludeUrls);
    this.includeUrls = new TrackedArray<string>(includeUrls);

    // Use the combined URLs from domain selection
    this.additionalUrls = additionalUrlDomains.uniq().join(',');

    // Check if the domain is selected and use it as the primary source URL if all pages are selected
    if (
      this.domain &&
      this.selectedDomains.some((d) => d.domain === this.domain?.domain) &&
      this.allDomainPagesAreSelected()
    ) {
      this.sourceUrl = this.domain.domain;
    } else if (!this.allDomainPagesAreSelected() && this.selectedDomains.length > 0) {
      let selectedUrls = this.selectedDomains?.firstObject?.startUrls;
      this.sourceUrl = selectedUrls?.firstObject || '';
    } else {
      this.sourceUrl = this.additionalUrlsArray.firstObject || '';
    }
  }

  @action
  handleSubdomainSelectionChange(selection: string) {
    let newSubdomains = [...this.subdomains, { domain: selection, pages: [] }];
    this.subdomains = newSubdomains;
    this.availableSubdomains = this.getAllOtherAvailableSubdomains(
      newSubdomains,
      this.allSubdomains,
    );

    this.rebuildUrlCollections();
  }

  checkHasPages(domain: SitemapDomain) {
    if (domain.pages.length === 0) {
      return false;
    }
    return true;
  }

  @task
  *fetchDomainPages(domain: SitemapDomain) {
    if (this.appService.app.canUseFeature('team-knowledge-foundations-root-url-sitemap-display')) {
      yield taskFor(this.fetchPageStructure).perform(domain.domain, true);
    } else {
      yield taskFor(this.fetchPageStructure).perform(domain.domain);
    }
  }

  get isLoadingDomainPages(): boolean {
    return taskFor(this.fetchDomainPages).isRunning;
  }

  get isLoadingSubdomains(): boolean {
    return taskFor(this.fetchSubdomains).isRunning;
  }

  private async checkUrl() {
    this.isLoadingUrl = true;
    try {
      let params = {
        url: this.sourceUrl,
        app_id: this.appService.app.id,
        admin_id: this.appService.app.currentAdmin.id,
      };

      let response = await get(`/ember/content_service/website_status/check`, params);
      this.urlCheckResponse = response as UrlCheckResponse;
      this.setIsSyncableWebsite(this.urlCheckResponse);
    } catch (e) {
      this.isLoadingUrl = false;
      return;
    }
    this.isLoadingUrl = false;
  }

  @task
  *fetchSubdomains(): TaskGenerator<void> {
    let params = {
      url: this.sourceUrl,
      app_id: this.appService.app.id,
      admin_id: this.appService.app.currentAdmin.id,
    };

    let response;

    try {
      response = yield get(`/ember/content_service/content_import_runs/find_subdomains`, params);
    } catch (e) {
      // Do nothing as subdomains will be set to blank array
    }

    let allSubdomains = response.subdomains.map((subdomain: string) => {
      return {
        domain: subdomain,
        pages: [],
      };
    });

    // Only display 3 subdomains at the start
    let subdomains = allSubdomains.slice(0, 3);

    this.subdomains = subdomains;
    this.allSubdomains = allSubdomains;
    this.availableSubdomains = this.getAllOtherAvailableSubdomains(
      this.subdomains,
      this.allSubdomains,
    );
  }

  private getAllOtherAvailableSubdomains(
    subdomains: SitemapDomain[],
    allSubdomains: SitemapDomain[],
  ) {
    let availableSubdomains = allSubdomains.filter(
      (subdomain: SitemapDomain) => !subdomains.some((s) => s.domain === subdomain.domain),
    );

    return availableSubdomains.map((subdomain: SitemapDomain) => {
      return {
        text: subdomain.domain,
        value: subdomain.domain,
      };
    });
  }

  @task
  *fetchPageStructure(url: string, isSubdomain = false): TaskGenerator<void> {
    let params: any = {
      url,
      app_id: this.appService.app.id,
      admin_id: this.appService.app.currentAdmin.id,
    };

    if (this.appService.app.canUseFeature('team-knowledge-foundations-root-url-sitemap-display')) {
      params.is_subdomain = isSubdomain;
    }

    let response;

    try {
      response = yield get(`/ember/content_service/content_import_runs/get_sitemap_tree`, params);
    } catch (e) {
      if (url === this.sourceUrl) {
        this.domain = {
          domain: this.sourceUrl,
          pages: [],
        };
      }
    }

    if (this.appService.app.canUseFeature('team-knowledge-foundations-root-url-sitemap-display')) {
      url = urlWithoutQueryParams(url);
    }

    let domain = {
      domain: url,
      pages: response.sitemap_tree,
    };

    if (this.appService.app.canUseFeature('team-knowledge-foundations-root-url-sitemap-display')) {
      let rootUrl = response.root_url ?? '';

      if (rootUrl && urlWithoutProtocolAndWww(rootUrl) !== urlWithoutProtocolAndWww(url)) {
        domain.domain = rootUrl;
        this.specificPageToSelect = response.sitemap_tree.find((page: SitemapPage) => {
          return urlWithoutProtocolAndWww(page.url) === urlWithoutProtocolAndWww(url);
        });
      }

      if (!this.domain && !isSubdomain) {
        this.domain = domain;
        if (!this.specificPageToSelect) {
          this.setDefaultDomainAsSelectedDomain(domain);
        }
      } else {
        this.updateOrAddSubdomain(url, response.sitemap_tree);
      }
    } else if (!this.domain && domain.domain === this.sourceUrl) {
      this.domain = domain;
      this.setDefaultDomainAsSelectedDomain(domain);
    } else {
      this.updateOrAddSubdomain(url, response.sitemap_tree);
    }
  }

  private updateOrAddSubdomain(url: string, pages: SitemapPage[]): void {
    // Find the subdomain in the subdomains array that matches the current URL
    let subdomainIndex = this.subdomains.findIndex((subdomain) => subdomain.domain === url);

    // Create a new array either updating the existing subdomain or adding a new one to trigger reactivity
    let updatedSubdomains = [...this.subdomains];

    // If found, update its pages
    if (subdomainIndex !== -1) {
      updatedSubdomains[subdomainIndex].pages = pages;
    } else {
      updatedSubdomains.push({
        domain: url,
        pages,
      });
    }

    this.subdomains = updatedSubdomains;
  }

  private setIsSyncableWebsite(data: UrlCheckResponse): void {
    this.isSyncableWebsite = data.is_accessible !== false;
  }

  private setDefaultDomainAsSelectedDomain(domain: SitemapDomain): void {
    this.selectedDomains.push({
      domain: domain.domain,
      selectedPages: [],
      startUrls: [],
      urlsToExclude: [],
      includeUrls: [],
      additionalUrls: [],
    });
  }

  private resetForm(): void {
    this.domain = null;
    this.subdomains = [];
    this.allSubdomains = [];
    this.availableSubdomains = [];
    this.selectedDomains = [];
    this.selectedPages = [];
    this.includeUrls = new TrackedArray<string>([]);
    this.additionalUrls = '';
    this.excludedUrls = new TrackedArray<string>([]);
    this.specificPageToSelect = undefined;
  }

  private getSourceParams(): SourceParams {
    let sourceParams: SourceParams = {
      app_id: this.appService.app.id,
      url: isValidUrlWithProtocol(this.sourceUrl)
        ? this.sourceUrl
        : urlWithHttpsPrefix(this.sourceUrl),
      additional_urls: this.cleanAdditionalUrls(),
      trigger_source: TriggerSources.AdminInitiated,
      content_type: EntityType.ExternalContent,
      admin_id: this.appService.app.currentAdmin.id,
      exclude_url_globs: this.excludedUrlGlobs,
      include_url_globs: this.includeUrls,
      remove_element_css_selectors: this.removeCssSelectorsArray,
      click_element_css_selector: this.clickCssSelector,
      use_sitemap: this.useSitemap,
      wait_for_selector: this.waitForSelector,
    };

    if (this.websiteName?.trim()) {
      sourceParams.website_name = this.websiteName.trim();
    }

    let secondsPerMonth = 30 * 24 * 60 * 60; // months to seconds
    let relativeStartDate = this.relativeDateMonths
      ? this.relativeDateMonths * secondsPerMonth
      : undefined;

    // Set actor data
    sourceParams.actor_data = {
      actor_name: this.useDateCrawler
        ? ActorName.DatedContentCrawler
        : ActorName.WebsiteContentCrawler,
      relative_start_date: relativeStartDate,
      date_selector: this.dateSelector,
      include_pages_with_no_date: this.includePagesWithNoDate,
    };

    return sourceParams;
  }

  @task
  *addNewSource(): TaskGenerator<void> {
    this.trackAnalyticsEvent('clicked', 'import_source_button');

    if (!this.isUrlInvalid) {
      try {
        this.submissionErrorTranslationKey = undefined;
        yield post(`/ember/content_service/content_import_runs`, this.getSourceParams());
        this.notificationsService.notifyConfirmation(
          this.intl.t('knowledge-hub.sync-modal.source.sync-started'),
        );
        this.contentImportService.subscribeToContentImportRunStatusUpdates();
        yield this.contentImportService.fetchContentImportSources();
        this.sourceUrl = '';
        this.additionalUrls = '';
        this.removeCssSelectors = '';
        this.clickCssSelector = '';
        this.waitForSelector = '';
        this.additionalUrlsArray.clear();
        this.excludedUrls = new TrackedArray<string>([]);
        this.useSitemap = true;
        this.args.onModalClose();

        if (this.guideLibraryService.canUseGuideLibraryService) {
          this.guideLibraryService.subscribeToContentImportUpdates();
        }
      } catch (e) {
        this.submissionErrorTranslationKey =
          this.contentImportService.submissionErrorTranslationKey(e);
      }
    }
  }

  @task
  *saveSource(): TaskGenerator<void> {
    this.trackAnalyticsEvent('clicked', 'save_button');

    if (!this.isUrlInvalid && !this.isAdditionalUrlsInvalid && this.syncSource) {
      try {
        yield this.syncSource.update(this.getSourceParams());
        this.contentImportService.subscribeToContentImportRunStatusUpdates();
        yield this.contentImportService.fetchContentImportSources();
        this.args.onModalClose();
      } catch (e) {
        this.submissionErrorTranslationKey =
          this.contentImportService.submissionErrorTranslationKey(e);
      }
    }
  }

  @task
  *saveAndResyncSource(): TaskGenerator<void> {
    this.trackAnalyticsEvent('clicked', 'save_and_resync_button');

    if (!this.isUrlInvalid && !this.isAdditionalUrlsInvalid && this.syncSource) {
      try {
        yield this.syncSource.update(this.getSourceParams());
        taskFor(this.syncSource.resync).perform();

        this.contentImportService.subscribeToContentImportRunStatusUpdates();
        yield this.contentImportService.fetchContentImportSources();
        this.args.onModalClose();
      } catch (e) {
        this.submissionErrorTranslationKey =
          this.contentImportService.submissionErrorTranslationKey(e);
      }
    }
  }

  @action onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  updateValues(params: addModalUpdateParams) {
    this.additionalUrls = params.additionalUrls;
    this.removeCssSelectors = params.removeCssSelectors;
    this.clickCssSelector = params.clickCssSelector;
    this.waitForSelector = params.waitForSelector;
    this.useSitemap = params.useSitemap;
    this.relativeDateMonths = params.relativeDateMonths;
    this.dateSelector = params.dateSelector;
    this.includePagesWithNoDate = params.includePagesWithNoDate ?? false;
    this.useDateCrawler = params.useDateCrawler ?? false;
  }

  @action
  addExcludedUrl() {
    this.excludedUrls.push('');
    this.trackAnalyticsEvent('clicked', 'add_url_glob_button');
  }

  @action
  toggleSubdomain(subdomain: string) {
    if (this.selectedSubdomains.includes(subdomain)) {
      this.selectedSubdomains = this.selectedSubdomains.filter((s) => s !== subdomain);
    } else {
      this.selectedSubdomains = [...this.selectedSubdomains, subdomain];
    }
  }

  @action
  updateExcludedUrl(index: number, event: InputEvent) {
    let glob = (event.target as HTMLInputElement).value;
    this.excludedUrls[index] = glob;
    this.trackAnalyticsEvent('inputed', 'url_glob_input');
  }

  @action
  deleteExcludedUrl(index: number) {
    this.excludedUrls.splice(index, 1);
    this.trackAnalyticsEvent('clicked', 'delete_url_glob_button');
  }

  @action
  toggleSitemap() {
    this.trackAnalyticsEvent('clicked', 'use_sitemap_switch');
  }

  get anySubdomainHasMultiplePages(): boolean {
    for (let subdomain of this.subdomains) {
      if (subdomain.pages.length > 0) {
        return true;
      }
    }
    return false;
  }

  private trackAnalyticsEvent(action: string, object: string): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      context: 'side_drawer',
      place: 'external_content',
      section: 'operator',
    });
  }

  private cleanAdditionalUrls(): string[] {
    let additionalUrls = this.additionalUrlsArray;

    if (this.sourceUrl && additionalUrls.includes(this.sourceUrl)) {
      additionalUrls = additionalUrls.filter((url) => url !== this.sourceUrl);
    }

    return additionalUrls;
  }

  private allDomainPagesAreSelected(): boolean {
    return this.selectedDomains.some(
      (d) =>
        d.domain === this.domain?.domain && d.selectedPages.length === this.domain?.pages.length,
    );
  }

  private isDomainSelected(): boolean {
    if (!this.domain) {
      return false;
    }
    return this.selectedDomains.some(
      (d) =>
        d.domain === this.domain!.domain &&
        (d.selectedPages?.length > 0 || d.startUrls?.length > 0),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentService::AiContentLibrary::Sources::AddSourceModal': typeof AddSourceModal;
  }
}
